import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest, resetPassword, getSupabaseClient } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/hooks/useToast'

interface Subscription {
  plan: {
    id: string
    name: string
    slug: string
    price_monthly: number
    links_per_month: number
    max_custom_domains: number
    api_requests_per_day: number
    analytics_retention_days: number
  }
}

interface Plan {
  id: string
  name: string
  slug: string
  price_monthly: number
}

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  last_used_at: string | null
}

interface NewApiKey {
  api_key: string
}

export function AccountPage() {
  const { user } = useAuthStore()
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '')
  const [keyName, setKeyName] = useState('')
  const [newApiKey, setNewApiKey] = useState<string | null>(null)

  // Fetch subscription
  const { data: subscription } = useQuery<Subscription>({
    queryKey: ['subscription'],
    queryFn: () => apiRequest('/api/billing/subscription')
  })

  // Fetch plans
  const { data: plans } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => apiRequest('/api/billing/plans')
  })

  // Fetch API keys
  const { data: apiKeys } = useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: () => apiRequest('/api/keys')
  })

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (newName: string) => {
      const supabase = await getSupabaseClient()
      const { error } = await supabase.auth.updateUser({ data: { full_name: newName } })
      if (error) throw error
    },
    onSuccess: () => {
      showToast('Profile updated successfully')
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update profile', 'error')
    }
  })

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: (name: string) => apiRequest<NewApiKey>('/api/keys', 'POST', { name }),
    onSuccess: (data) => {
      setNewApiKey(data.api_key)
      setKeyName('')
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      showToast('API Key created successfully')
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to create API key', 'error')
    }
  })

  // Delete API key mutation
  const deleteKeyMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/keys/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      showToast('API Key revoked')
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to revoke API key', 'error')
    }
  })

  const handleUpdateProfile = () => {
    if (fullName.trim()) {
      updateProfileMutation.mutate(fullName.trim())
    }
  }

  const handlePasswordReset = async () => {
    if (user?.email) {
      try {
        await resetPassword(user.email)
        showToast('Password reset email sent')
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Failed to send reset email', 'error')
      }
    }
  }

  const handleCreateKey = (e: FormEvent) => {
    e.preventDefault()
    if (keyName.trim()) {
      createKeyMutation.mutate(keyName.trim())
    }
  }

  const handleDeleteKey = (id: string) => {
    if (confirm('Revoke this API key?')) {
      deleteKeyMutation.mutate(id)
    }
  }

  const handleUpgrade = async () => {
    if (!plans || !subscription) return

    const nextPlan = plans.find(p => p.price_monthly > (subscription.plan.price_monthly || 0))
    if (!nextPlan) {
      showToast("You're already on our highest plan!", 'info')
      return
    }

    try {
      const { checkout_url } = await apiRequest<{ checkout_url: string }>('/api/billing/checkout', 'POST', { plan_id: nextPlan.id })
      if (checkout_url) {
        window.location.href = checkout_url
      } else {
        showToast('Stripe is not configured.', 'error')
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to start checkout', 'error')
    }
  }

  const plan = subscription?.plan
  const isMaxPlan = plan?.slug === 'business'

  return (
    <>
      {/* Page Heading */}
      <div className="mb-8">
        <h2 className="text-[#0d121b] dark:text-white text-3xl font-black tracking-tight">Settings</h2>
        <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">
          Manage your account and preferences.
        </p>
      </div>

      {/* Profile Section */}
      <div className="bg-white dark:bg-[#0d121b] p-6 rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm mb-6">
        <h3 className="text-base font-bold mb-4 text-[#0d121b] dark:text-white">Profile</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 items-center">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Full Name</label>
            <div className="col-span-2 flex gap-2">
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="flex-1 h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:border-[#1754cf] focus:ring-1 focus:ring-[#1754cf] text-[#0d121b] dark:text-white"
              />
              <button
                onClick={handleUpdateProfile}
                disabled={updateProfileMutation.isPending}
                className="h-10 px-4 text-sm font-semibold bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition-colors text-[#0d121b] dark:text-white"
              >
                Save
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 items-center">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
            <div className="col-span-2">
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm text-gray-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 items-center">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Password</label>
            <div className="col-span-2">
              <button
                onClick={handlePasswordReset}
                className="h-10 px-4 text-sm font-semibold border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 rounded-lg transition-colors text-[#0d121b] dark:text-white"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Section */}
      <div className="bg-white dark:bg-[#0d121b] p-6 rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm mb-6">
        <h3 className="text-base font-bold mb-4 text-[#0d121b] dark:text-white">Subscription</h3>
        <div className="bg-gradient-to-r from-[#1754cf]/10 to-blue-500/10 p-4 rounded-lg border border-[#1754cf]/20 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-lg font-bold text-[#0d121b] dark:text-white">
                {plan?.name || 'Free'} Plan
              </p>
              <p className="text-sm text-gray-500">
                {plan?.price_monthly === 0 ? 'Free' : `$${(plan?.price_monthly || 0) / 100}/month`}
              </p>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={isMaxPlan}
              className="h-10 px-4 text-sm font-bold bg-[#1754cf] text-white rounded-lg hover:bg-[#1754cf]/90 transition-colors disabled:opacity-50"
            >
              {isMaxPlan ? 'Max Plan' : 'Upgrade'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xl font-bold text-[#1754cf]">
              {plan?.links_per_month?.toLocaleString() || 50}
            </p>
            <p className="text-xs text-gray-500">Links/mo</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xl font-bold text-[#1754cf]">
              {plan?.max_custom_domains || 0}
            </p>
            <p className="text-xs text-gray-500">Domains</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xl font-bold text-[#1754cf]">
              {plan?.api_requests_per_day?.toLocaleString() || 100}
            </p>
            <p className="text-xs text-gray-500">API/day</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xl font-bold text-[#1754cf]">
              {plan?.analytics_retention_days === -1 ? '∞' : `${plan?.analytics_retention_days || 7}d`}
            </p>
            <p className="text-xs text-gray-500">Analytics</p>
          </div>
        </div>
      </div>

      {/* API Keys Section */}
      <div className="bg-white dark:bg-[#0d121b] p-6 rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm mb-6">
        <h3 className="text-base font-bold mb-4 text-[#0d121b] dark:text-white">API Keys</h3>
        <p className="text-sm text-gray-500 mb-4">Generate keys to access the API programmatically.</p>

        {/* New Key Alert */}
        {newApiKey && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-4">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400">New API Key Created</p>
            <code className="block mt-2 p-2 bg-white dark:bg-gray-900 rounded text-xs break-all text-[#0d121b] dark:text-white">
              {newApiKey}
            </code>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
              Save this key now. You won't see it again!
            </p>
          </div>
        )}

        <form onSubmit={handleCreateKey} className="flex gap-2 mb-4">
          <input
            type="text"
            value={keyName}
            onChange={e => setKeyName(e.target.value)}
            placeholder="Key name (e.g., Production)"
            required
            className="flex-1 h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-[#0d121b] dark:text-white placeholder:text-gray-400"
          />
          <button
            type="submit"
            disabled={createKeyMutation.isPending}
            className="h-10 px-4 text-sm font-semibold bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition-colors text-[#0d121b] dark:text-white"
          >
            Create Key
          </button>
        </form>

        <div className="space-y-2">
          {apiKeys && apiKeys.length > 0 ? (
            apiKeys.map(key => (
              <div
                key={key.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-[#0d121b] dark:text-white">{key.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{key.key_prefix}...</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {key.last_used_at
                      ? `Used ${new Date(key.last_used_at).toLocaleDateString()}`
                      : 'Never used'}
                  </span>
                  <button
                    onClick={() => handleDeleteKey(key.id)}
                    className="text-sm text-red-500 hover:underline"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No API keys yet.</p>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white dark:bg-[#0d121b] p-6 rounded-xl border border-red-200 dark:border-red-900/30 shadow-sm">
        <h3 className="text-base font-bold text-red-600 mb-4">Danger Zone</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#0d121b] dark:text-white">Delete Account</p>
            <p className="text-xs text-gray-500">Permanently delete your account and all data.</p>
          </div>
          <button className="h-10 px-4 text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
            Delete Account
          </button>
        </div>
      </div>
    </>
  )
}
